import React from 'react'
import { SwitchContentWrapper, Title } from './styled'
import { Accordion, Fonts, Text, TextSize } from 'components'
import { AnalyticsArea, useAnalyticsEventTracker } from 'hooks'
import { useParams } from 'react-router-dom'
import { getProductByAddress } from 'indices-list'
import styled from 'styled-components'
import { assetsListBySymbol } from 'abis/set/asset-icons'

const StyledAccordion = styled(Accordion)`
  margin-top: 35px;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.text1};
  opacity: 0.6;
  font-family: ${Fonts.DMSans};
  font-size: ${TextSize.small};
`

const StyledLink = styled.a`
  color: ${({ theme }) => theme.text1};
  font-family: ${Fonts.DMSans};
  font-size: ${TextSize.small};
  text-decoration: underline;
  cursor: pointer;
  transition: 0.4s all;
  :hover {
    opacity: 0.7;
  }
  :active {
    opacity: 0.4;
  }
`

const data = (
  link: string,
  relocate: (link: string) => void,
): { [key: string]: { title: string; content: string | JSX.Element }[] } => {
  return {
    JMX: [
      {
        title: 'What are the benefits of an index?',
        content:
          'An index makes it easy to benefit from the rise of a market, trend or sector without having to pick individual winners. It instantly gives your portfolio diversification by providing exposure to a basket of assets through a single token and saves you money by covering all rebalancing costs.',
      },
      {
        title: 'Can I buy and sell at any time?',
        content: 'JMX can be bought or sold 24/7.',
      },
      {
        title: 'How does the index hold assets across multiple chains?',
        content:
          'Phuture is the first onchain index provider to support cross chain collateral through major breakthroughs in multi-chain trading and accounting. Cross-chain messages are securely sent via Layerzero.',
      },
      // {
      //   title: 'How are my funds invested?',
      //   content:
      //     'Funds deposited into the index are held in ETH and periodically swapped into the constituent assets of the index. This ensures a low cost experience when minting and redeeming.',
      // },
      {
        title: 'Can I use JMX in other DeFi products?',
        content:
          'Yes! JMX is a token that conforms to the recognised ERC-20 standard and can be used on exchanges, money markets, derivatives platforms and more.',
      },
      {
        title: 'How often is the index rebalanced?',
        content: 'The index is rebalanced every two weeks.',
      },
      {
        title: 'Who decides what goes into the index?',
        content: (
          <StyledText>
            {`Every memecoin included in the index must first pass JOOCE's stringent set of listing criteria which can be found `}
            <StyledLink
              onClick={() => relocate('https://docs.jooce.xyz/jmx-governance/listing-criteria')}
            >
              here
            </StyledLink>
            {
              '. The $JOOCE community can then vote for any of the approved assets. The more votes a coin gets the larger its weight will be in the next rebalancing. You can view the real time voting '
            }
            <StyledLink onClick={() => relocate('https://jooce.xyz/voting')}>here</StyledLink>
          </StyledText>
        ),
      },
    ],
    ARTXAI: [
      {
        title: 'What are the benefits of investing on ARTX AI Fund?',
        content:
          'ARTX AI Fund allows you to have dynamic exposure to Bitcoin, Ethereum and USDC. ARTX AI models will choose at any given moment the proportion in which these three assets will be invested, which in the long run will offer investors a better risk-weighted return than investing in these assets individually.',
      },
      {
        title: 'How do the AI systems used by the fund work?',
        content:
          'These are proprietary systems and models developed by ARTX, which incorporate market data such as prices or volumes, as well as blockchain data and social sentiment to periodically deliver an optimal investment portfolio that is applied directly to the fund`s capital.',
      },
      {
        title: 'How often is the fund rebalanced?',
        content:
          'The fund is rebalanced multiple times each day according to its models, always maintaining the allocation decided by the ARTX AI systems.',
      },
      {
        title: 'Can I buy and sell at any time?',
        content:
          'Yes! We’ve established 24/7 liquid markets for all our products which means you can always enter or exit a position of any size.',
      },
      {
        title: 'Can I use ARTXAI in other DeFi products?',
        content:
          'Yes! ARTXAI is a token that conforms to the recognised ERC-20 standard and can be used on exchanges, money markets, derivatives platforms and more.',
      },
    ],
    CAI: [
      {
        title: 'What are the benefits of an index?',
        content:
          'An index makes it easy to benefit from the rise of a market without having to pick individual winners. It instantly gives your portfolio diversification by providing exposure to a basket of assets through a single token and saves you money by covering all rebalancing costs.',
      },
      {
        title: 'Can I buy and sell at any time?',
        content:
          'Yes! We’ve established 24/7 liquid markets for all our products which means you can always enter or exit a position of any size.',
      },
      {
        title: 'Can I use CAI in other DeFi products?',
        content:
          'Yes! CAI is a token that conforms to the recognised ERC-20 standard and can be used on exchanges, money markets, derivatives platforms and more.',
      },
      {
        title: 'Where are the assets backing CAI stored? ',
        content:
          'The assets are stored on smart contracts that only CAI token holders can withdraw their pro-rata share from.',
      },
      {
        title: 'How often is the index rebalanced?',
        content: (
          <StyledText>
            The index is rebalanced monthly according to its{' '}
            <StyledLink onClick={() => relocate(link)}>methodology</StyledLink>, where new assets
            are added and old assets are removed.
          </StyledText>
        ),
      },
      {
        title: 'Who decides what goes into the index?',
        content: (
          <StyledText>
            The index follows a <StyledLink onClick={() => relocate(link)}>methodology</StyledLink>{' '}
            that was carefully developed to provide comprehensive exposure to the Avalanche
            ecosystem. All assets added to the index must meet the requirements set out in the
            methodology to ensure the highest level of quality.
          </StyledText>
        ),
      },
    ],
  }
}

export const FaqContent: React.FC = () => {
  const { clickEvent, navigationEvent } = useAnalyticsEventTracker(AnalyticsArea.FAQ)
  const { symbol: productSymbol } = useParams<{ symbol: string }>()
  const productAddress = assetsListBySymbol[productSymbol?.toUpperCase() as string].address
  const { symbol, methodologyLink } = getProductByAddress(productAddress)
  const relocate = (link: string) => navigationEvent(link, true)
  const faqData = data(methodologyLink, relocate)[symbol.toUpperCase()]

  return (
    <SwitchContentWrapper>
      <Title>FAQ</Title>
      <StyledAccordion accordionContent={faqData} analyticsClickEvent={clickEvent} />
    </SwitchContentWrapper>
  )
}
